import { useSyncExternalStore } from 'react'
import type { Author, Story } from '../data/content'

export type CmsSnapshot = {
  stories: Story[]
  authors: Author[]
  version: number
  loaded: boolean
  live: boolean
  cardsIndexed: boolean
}

type CmsAls = { getStore: () => CmsSnapshot | undefined }

function alsSnapshot() {
  return (globalThis as { __TIGG_CMS_ALS__?: CmsAls }).__TIGG_CMS_ALS__?.getStore()
}

let snapshot: CmsSnapshot = { stories: [], authors: [], version: 0, loaded: false, live: false, cardsIndexed: false }
const listeners = new Set<() => void>()

function emit() {
  listeners.forEach((listener) => listener())
}

export function subscribeCms(onStoreChange: () => void) {
  listeners.add(onStoreChange)
  return () => {
    listeners.delete(onStoreChange)
  }
}

export function getCmsSnapshot() {
  return alsSnapshot() ?? snapshot
}

export function liveStories() {
  return getCmsSnapshot().stories
}

export function liveAuthors() {
  return getCmsSnapshot().authors
}

export function applyCmsFeed(feed: { stories?: Story[]; authors?: Author[] }, options?: { cardsIndexed?: boolean }) {
  snapshot = {
    stories: feed.stories ?? [],
    authors: feed.authors ?? [],
    version: snapshot.version + 1,
    loaded: true,
    live: true,
    cardsIndexed: Boolean(options?.cardsIndexed),
  }
  emit()
}

function keepBody(prev: Story, next: Story): Story {
  if (next.bodyHtml || next.body?.length) return next
  if (!prev.bodyHtml && !prev.body?.length) return next
  return {
    ...next,
    body: prev.body,
    bodyHtml: prev.bodyHtml,
    ingredients: prev.ingredients,
    isRecipe: prev.isRecipe,
    imageCredit: prev.imageCredit,
    metaDescription: prev.metaDescription,
  }
}

function mergeStories(incoming: Story[]) {
  const map = new Map(snapshot.stories.map((story) => [story.slug, story]))
  for (const next of incoming) {
    const prev = map.get(next.slug)
    map.set(next.slug, prev ? keepBody(prev, next) : next)
  }
  return [...map.values()]
}

export function mergeCmsCards(feed: { stories?: Story[]; authors?: Author[] }) {
  snapshot = {
    ...snapshot,
    stories: mergeStories(feed.stories ?? []),
    authors: feed.authors?.length ? feed.authors : snapshot.authors,
    version: snapshot.version + 1,
    loaded: true,
    live: true,
    cardsIndexed: true,
  }
  emit()
}

export function rememberStory(story: Story, extras?: { related?: Story[]; author?: Author | null }) {
  const authors =
    extras?.author && !snapshot.authors.some((item) => item.slug === extras.author?.slug)
      ? [...snapshot.authors, extras.author]
      : snapshot.authors
  snapshot = {
    ...snapshot,
    stories: mergeStories([story, ...(extras?.related ?? [])]),
    authors,
    loaded: true,
    live: true,
  }
}

function markCmsLoaded() {
  if (snapshot.loaded) return
  snapshot = { ...snapshot, loaded: true, version: snapshot.version + 1 }
  emit()
}

let cardsRequest: Promise<void> | undefined

/** Card index only. Post bodies come from /api/admin/posts/:id and /api/public/story/:slug. */
export function refreshCmsFeed() {
  cardsRequest = undefined
  return fetch('/api/public/feed', { cache: 'no-store' })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (data?.stories) mergeCmsCards(data)
      else markCmsLoaded()
    })
    .catch(() => {
      markCmsLoaded()
    })
}

export function ensureCmsCards() {
  if (getCmsSnapshot().cardsIndexed || snapshot.cardsIndexed) return Promise.resolve()
  if (!cardsRequest) {
    cardsRequest = fetch('/api/public/feed')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.stories) mergeCmsCards(data)
        else markCmsLoaded()
      })
      .catch(() => {
        markCmsLoaded()
      })
      .finally(() => {
        cardsRequest = undefined
      })
  }
  return cardsRequest
}

export function cmsVersion() {
  return getCmsSnapshot().version
}

export function cmsLive() {
  return getCmsSnapshot().live
}

export function cmsLoaded() {
  return getCmsSnapshot().loaded
}

export function useCmsVersion() {
  return useSyncExternalStore(
    subscribeCms,
    () => getCmsSnapshot().version,
    () => getCmsSnapshot().version,
  )
}
